import React from 'react'

import { updateUserLocale } from '../../api'
import SideBar from '../../components/ui/layout/sidebar'
import GoBackButton from '../../components/ui/button/goBack'
import { useAuth } from '../../context/auth'
import PageSectionTitle from '../../components/ui/pageSectionTitle'
import { PageHeaderProvider, usePageHeaderOptional } from '../../context/pageHeader'
import { LayoutPanelsProvider } from '../../context/layoutPanels'
import PageRightRailLayout from '../../components/ui/layout/pageRightRail/PageRightRailLayout'
import './styles.scss'

const MasterPageHeader: React.FC<{
  header?: string
  description?: string
  actions?: React.ReactElement
  backTo?: string
  hideBackButton?: boolean
}> = ({ header, description, actions, backTo, hideBackButton }) => {
  const pageHeader = usePageHeaderOptional()
  const registered = pageHeader?.config

  const resolvedTitle = registered?.title ?? header
  const resolvedDescription = registered?.description ?? description
  const resolvedActions = registered?.actions !== undefined ? registered.actions : actions
  const resolvedTitleClassName = registered?.titleClassName
  const resolvedBackTo = registered?.backTo ?? backTo
  const resolvedHideBackButton = registered?.hideBackButton ?? hideBackButton

  return (
    <div className='master-page-header'>
      {!resolvedHideBackButton && (
        <div className='master-page-header__back d-none d-md-block'>
          <GoBackButton backTo={resolvedBackTo} />
        </div>
      )}
      <div className={['master-page-header__title', resolvedTitleClassName].filter(Boolean).join(' ')}>
        {resolvedTitle && (
          <PageSectionTitle
            title={resolvedTitle}
            description={resolvedDescription}
          />
        )}
      </div>
      {resolvedActions ? (
        <div className='master-page-header__actions'>{resolvedActions}</div>
      ) : null}
    </div>
  )
}

type Props = {
  children: React.ReactNode,
  header?: string,
  description?: string,
  actions?: React.ReactElement,
  hideMenus?: boolean,
  backTo?: string
  hideBackButton?: boolean
  stickyHeader?: boolean
  rightRail?: React.ReactNode
  contentBodyClassName?: string
}
const MasterPage: React.FC<Props> = ({
  children,
  header,
  description,
  hideMenus,
  actions,
  backTo,
  hideBackButton,
  stickyHeader = false,
  rightRail,
  contentBodyClassName,
}) => {
  const { user, mutate } = useAuth()
  const hasSetDefaultLocaleRef = React.useRef(false)

  React.useEffect(() => {
    if (!user?.locale && user?._id && !hasSetDefaultLocaleRef.current) {
      hasSetDefaultLocaleRef.current = true
      const deviceLocale = typeof navigator !== 'undefined' ? navigator.language : 'en-GB'
      updateUserLocale(deviceLocale).then((res) => {
        if (res?.data) mutate()
      })
    }
  }, [user?._id, user?.locale, mutate])

  return (
    <PageHeaderProvider>
      <LayoutPanelsProvider>
        <div>
          <SideBar
          hideMenus={hideMenus}
          headerContent={(
            <MasterPageHeader
              header={header}
              description={description}
              actions={actions}
              backTo={backTo}
              hideBackButton={hideBackButton}
            />
          )}
        />
        <div className='page-content bg-white'>
          {rightRail ? (
            <PageRightRailLayout rail={rightRail}>
              <div
                className={[
                  'content-header',
                  stickyHeader && 'content-header--sticky',
                ].filter(Boolean).join(' ')}
              >
                <div className='content-header__inner align-items-center d-md-flex d-inline-flex'>
                  <MasterPageHeader
                    header={header}
                    description={description}
                    actions={actions}
                    backTo={backTo}
                    hideBackButton={hideBackButton}
                  />
                </div>
              </div>
              <div
                className={[
                  'content-body',
                  'mb-5',
                  'mb-md-0',
                  contentBodyClassName,
                ].filter(Boolean).join(' ')}
              >
                {children}
              </div>
            </PageRightRailLayout>
          ) : (
            <>
              <div
                className={[
                  'content-header',
                  stickyHeader && 'content-header--sticky',
                ].filter(Boolean).join(' ')}
              >
                <div className='content-header__inner align-items-center d-md-flex d-inline-flex'>
                  <MasterPageHeader
                    header={header}
                    description={description}
                    actions={actions}
                    backTo={backTo}
                    hideBackButton={hideBackButton}
                  />
                </div>
              </div>
              <div className='content-body mb-5 mb-md-0'>
                {children}
              </div>
            </>
          )}
        </div>
        </div>
      </LayoutPanelsProvider>
    </PageHeaderProvider>
  )
}

export default MasterPage
